import { FC, MouseEvent, useCallback, useRef, useState } from 'react'

import { excludedClassName, useOnClickOutside } from '../../hooks'
import { useIsMobile } from '../../utils'
import * as S from './ConfirmAction.styles'
import { ConfirmActionProps } from './ConfirmAction.types'
import { ConfirmActionContent } from './ConfirmActionContent'

export const ConfirmAction: FC<ConfirmActionProps> = ({
  title,
  message,
  onConfirm,
  onClick = () => null,
  onToggleMessage,
  children,
  horizontalAlignment,
  verticalAlignment,
  containerStyle,
  /**
   * Positive offset makes the container go down
   * Negative offset makes the container go up
   */
  offsetHeight = 0,
  theme,
  cancelText,
  confirmText,
  confirmButtonType,
  focusOnDisplay = false,
  isSubmitButtonDisabled = false,
  stopPropagation = false,
}) => {
  const [showMessage, setShowMessage] = useState(false)
  const [containerPosition, setContainerPosition] = useState<number | undefined>(undefined)

  const isMobile = useIsMobile()

  const innerRef = useRef<HTMLDivElement | null>(null)

  const handleHideMessage = useCallback(
    (e?: MouseEvent) => {
      if (e && stopPropagation) {
        e.stopPropagation()
      }
      setShowMessage(false)
      if (onToggleMessage) {
        onToggleMessage(false)
      }
    },
    [stopPropagation, onToggleMessage],
  )

  const handleShowMessage = useCallback(() => {
    setShowMessage(true)
    if (onToggleMessage) {
      onToggleMessage(true)
    }
  }, [onToggleMessage])

  const handleOnActionConfirmed = useCallback(
    (e?: MouseEvent) => {
      if (e && stopPropagation) {
        e.stopPropagation()
      }
      onConfirm?.()
      handleHideMessage()
    },
    [stopPropagation, onConfirm, handleHideMessage],
  )

  const handleOnClickAction = useCallback(
    (e: any) => {
      if (e && stopPropagation) {
        e.stopPropagation()
      }
      onClick(e)
      handleShowMessage()
    },
    [stopPropagation, handleShowMessage, onClick],
  )

  useOnClickOutside(innerRef, handleHideMessage)

  if (showMessage && focusOnDisplay) {
    innerRef.current?.scrollIntoView({ block: 'center', inline: 'nearest' })
  }

  const Wrapper = isMobile ? S.FloatingContainerMobile : S.FloatingContainerStyled

  return (
    <S.Container style={containerStyle}>
      <S.ChildrenContainer
        onClick={handleOnClickAction}
        ref={el => {
          if (!el) return
          setContainerPosition(el.getBoundingClientRect().y + offsetHeight)
        }}
      >
        {children}
      </S.ChildrenContainer>

      <Wrapper
        placement={horizontalAlignment}
        customVerticalAlignment={verticalAlignment}
        verticalValue={containerPosition}
        open={showMessage}
        onClickOut={handleHideMessage}
        className={excludedClassName}
      >
        <div ref={innerRef}>
          <ConfirmActionContent
            customVerticalAlignment={verticalAlignment}
            message={message}
            onCancel={handleHideMessage}
            onConfirm={handleOnActionConfirmed}
            title={title}
            theme={theme}
            cancelText={cancelText}
            confirmText={confirmText}
            confirmButtonType={confirmButtonType}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
          />
        </div>
      </Wrapper>
    </S.Container>
  )
}
